import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Renders a narrow tick-mark strip alongside the Ask-mode response scroll area.
 *
 * Each history entry gets two 3×6 px ticks:
 *   Left  (green)  — Expansive response indicator
 *   Right (purple) — Evaluation report indicator
 *
 * Solid colour  → the response exists; click to navigate there.
 * Faded colour  → the response is missing but can be generated;
 *                  click to navigate AND open the generation dialog.
 *                  (Blocked when isRunning=true to match the Response buttons.)
 * Very faded    → not yet unlockable (evaluation needs expansive first).
 *
 * DOM nodes are intentionally NOT stored in state to avoid stale-ref bugs on
 * rapid conversation switches. Instead, only the entry key and proportional
 * scroll position are stored; the element is looked up fresh at click time.
 */
const ScrollIndicatorStrip = ({
  containerRef,
  chatHistory,
  onExpansiveClick,
  onEvalClick,
  isRunning = false,
}) => {
  const [ticks, setTicks] = useState([]);
  const rafRef = useRef(null);
  const moTimerRef = useRef(null);

  const measure = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const container = containerRef.current;
      if (!container || container.scrollHeight === 0) return;

      const totalHeight = container.scrollHeight;
      const newTicks = chatHistory.flatMap((entry) => {
        const key = entry.index ?? 0;
        const el = container.querySelector(`[data-entry-index="${key}"]`);
        if (!el) return [];

        const hasPreliminary = Boolean(
          entry.preliminary_answer && entry.preliminary_answer !== entry.assistant
        );
        const hasEvaluation = Boolean(entry.evaluation_report);

        return [{
          key,
          position: el.offsetTop / totalHeight,
          hasPreliminary,
          hasEvaluation,
          entryIndex: entry.index,
          // entryKey stored for fresh lookup at click time — no live DOM ref held
        }];
      });

      setTicks(newTicks);
    });
  }, [containerRef, chatHistory]);

  useEffect(() => {
    measure();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [measure]);

  // ResizeObserver: catches container layout changes (viewport resize, panel resize).
  // MutationObserver (throttled): catches inner content reflows such as code blocks
  // and images loading that change scrollHeight without resizing the container box.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(measure);
    ro.observe(container);

    const handleMutation = () => {
      if (moTimerRef.current) return;
      moTimerRef.current = setTimeout(() => {
        moTimerRef.current = null;
        measure();
      }, 200);
    };
    const mo = new MutationObserver(handleMutation);
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      ro.disconnect();
      mo.disconnect();
      if (moTimerRef.current) { clearTimeout(moTimerRef.current); moTimerRef.current = null; }
    };
  }, [containerRef, measure]);

  // Look up the DOM element fresh at click time to avoid stale refs.
  const scrollToEntry = useCallback((entryKey) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-entry-index="${entryKey}"]`);
    if (el) container.scrollTo({ top: Math.max(0, el.offsetTop - 16), behavior: 'smooth' });
  }, [containerRef]);

  if (ticks.length === 0) return null;

  return (
    <div
      role="group"
      aria-label="Response entry quick-navigation"
      style={{
        width: 10,
        flexShrink: 0,
        position: 'relative',
        borderLeft: '1px solid var(--border-color, rgba(0,0,0,0.08))',
        userSelect: 'none',
      }}
    >
      {ticks.map((tick) => {
        const topPct = `${tick.position * 100}%`;

        const expansiveColor = tick.hasPreliminary
          ? '#28a745'
          : 'rgba(40,167,69,0.32)';

        const evalColor = tick.hasEvaluation
          ? 'var(--primary-color, #636cff)'
          : tick.hasPreliminary
            ? 'rgba(99,108,255,0.35)'
            : 'rgba(150,150,150,0.18)';

        const expansiveTip = tick.hasPreliminary
          ? 'Expansive response — click to jump'
          : isRunning
            ? 'Expansive not generated — finish current query first'
            : 'No expansive yet — click to jump & generate';

        const evalTip = tick.hasEvaluation
          ? 'Evaluation report — click to jump'
          : tick.hasPreliminary
            ? isRunning
              ? 'Evaluation not generated — finish current query first'
              : 'No evaluation yet — click to jump & generate'
            : 'Generate Expansive first to unlock Evaluation';

        const evalClickable = (tick.hasEvaluation || tick.hasPreliminary) && !isRunning;
        const canGenerateExpansive = !tick.hasPreliminary && !isRunning;
        const canGenerateEval = !tick.hasEvaluation && tick.hasPreliminary && !isRunning;

        const tickBase = {
          position: 'absolute',
          top: topPct,
          height: 6,
          width: 3,
          borderRadius: 2,
          transform: 'translateY(-50%)',
          transition: 'filter 0.15s',
        };

        return (
          <React.Fragment key={tick.key}>
            {/* Expansive tick — left column */}
            <button
              type="button"
              aria-label={expansiveTip}
              title={expansiveTip}
              style={{
                ...tickBase, left: 1,
                backgroundColor: expansiveColor,
                cursor: tick.hasPreliminary || canGenerateExpansive ? 'pointer' : 'default',
                border: 'none', padding: 0, appearance: 'none',
              }}
              onClick={(e) => {
                e.stopPropagation();
                scrollToEntry(tick.key);
                if (canGenerateExpansive && onExpansiveClick) onExpansiveClick(tick.entryIndex);
              }}
              onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
            />

            {/* Evaluation tick — right column */}
            <button
              type="button"
              aria-label={evalTip}
              aria-disabled={!evalClickable}
              title={evalTip}
              style={{
                ...tickBase, left: 6,
                backgroundColor: evalColor,
                cursor: evalClickable ? 'pointer' : 'default',
                border: 'none', padding: 0, appearance: 'none',
              }}
              onClick={(e) => {
                if (!evalClickable) return;
                e.stopPropagation();
                scrollToEntry(tick.key);
                if (canGenerateEval && onEvalClick) onEvalClick(tick.entryIndex);
              }}
              onMouseEnter={(e) => { if (evalClickable) e.currentTarget.style.filter = 'brightness(1.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ScrollIndicatorStrip;
